import { useMemo } from 'react'

/**
 * Full-bleed maritime hero backdrop — deep-ocean gradient, sea-chart grid,
 * paper grain, a slowly-turning compass rose, a charted treasure route, drifting
 * gulls, a moonlit galleon and three layers of animated swell.
 *
 * Pure CSS + inline SVG (no image assets). All motion is gated on
 * `prefers-reduced-motion`. Drop it as the first child of a `relative`,
 * `overflow-hidden` hero section; it sits behind your content (z-0..6).
 *
 * Reusable on /login & /register for a consistent maritime frame.
 */
export function SeaBackdrop({ shipAlign = 'center' }: { shipAlign?: 'center' | 'side' }) {
  // On the auth screens the centred card sits right on top of the galleon;
  // `shipAlign="side"` sails it into the right margin on wide screens so the
  // animation stays visible (mobile keeps it centred, revealed below the card).
  const shipPos =
    shipAlign === 'side'
      ? 'left-1/2 -translate-x-1/2 lg:left-auto lg:right-[2%] lg:translate-x-0'
      : 'left-1/2 -translate-x-1/2'

  // 5° degree ticks for the compass rose (major every 45°).
  const ticks = useMemo(
    () =>
      Array.from({ length: 72 }, (_, i) => {
        const a = (i / 72) * Math.PI * 2
        const r1 = 86
        const r2 = i % 9 === 0 ? 76 : 82
        return {
          x1: 100 + Math.sin(a) * r1,
          y1: 100 - Math.cos(a) * r1,
          x2: 100 + Math.sin(a) * r2,
          y2: 100 - Math.cos(a) * r2,
        }
      }),
    [],
  )

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none select-none" aria-hidden="true">
      <style>{BACKDROP_CSS}</style>

      {/* deep-sea gradient */}
      <div className="absolute inset-0 sea-bg" />
      {/* sea-chart grid */}
      <div className="absolute inset-0 chart-grid opacity-[0.05]" />
      {/* paper grain */}
      <div className="absolute inset-0 grain opacity-[0.05] mix-blend-overlay" />

      {/* compass rose watermark */}
      <div className="absolute left-1/2 top-[42%] -translate-x-1/2 -translate-y-1/2 w-[min(115vw,1080px)] aspect-square opacity-[0.09]">
        <svg viewBox="0 0 200 200" className="compass-rose w-full h-full" fill="none">
          <g stroke="#E5CC8F" fill="none">
            <circle cx="100" cy="100" r="96" strokeWidth="0.5" />
            <circle cx="100" cy="100" r="86" strokeWidth="0.6" />
            <circle cx="100" cy="100" r="48" strokeWidth="0.4" />
          </g>
          <g stroke="#E5CC8F" strokeWidth="0.5">
            {ticks.map((t, i) => (
              <line key={i} x1={t.x1.toFixed(1)} y1={t.y1.toFixed(1)} x2={t.x2.toFixed(1)} y2={t.y2.toFixed(1)} />
            ))}
          </g>
          {/* intercardinal (short) faceted points */}
          {[45, 135, 225, 315].map((deg) => (
            <g key={deg} transform={`rotate(${deg} 100 100)`}>
              <path d="M100,50 L94,86 L100,100 Z" fill="#C9A55C" opacity="0.85" />
              <path d="M100,50 L106,86 L100,100 Z" fill="#7A3E1F" opacity="0.55" />
            </g>
          ))}
          {/* cardinal (long) faceted points */}
          {[0, 90, 180, 270].map((deg) => (
            <g key={deg} transform={`rotate(${deg} 100 100)`}>
              <path d="M100,12 L92,80 L100,100 Z" fill="#F5EDD7" opacity="0.95" />
              <path d="M100,12 L108,80 L100,100 Z" fill="#C9A55C" opacity="0.65" />
            </g>
          ))}
          <circle cx="100" cy="100" r="7" fill="#E5CC8F" />
          <circle cx="100" cy="100" r="3.4" fill="#0A3E4F" />
          <path d="M97,7 L97,1 L103,1 L103,7" stroke="#E5CC8F" strokeWidth="0.8" fill="none" />
        </svg>
      </div>

      {/* charted route + treasure X */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.22]" viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice">
        <path d="M120,250 C360,140 560,360 760,300 S1180,180 1340,330" stroke="#E5CC8F" strokeWidth="2" strokeDasharray="3 12" strokeLinecap="round" fill="none" />
        <g stroke="#E5CC8F" strokeWidth="3" strokeLinecap="round">
          <line x1="1326" y1="316" x2="1354" y2="344" />
          <line x1="1354" y1="316" x2="1326" y2="344" />
        </g>
        <circle cx="120" cy="250" r="4" fill="#E5CC8F" />
      </svg>

      {/* gulls */}
      {GULLS.map((g, i) => (
        <svg
          key={i}
          className="absolute text-sand-300 gull"
          style={{ left: g.left, top: g.top, width: g.w, opacity: g.o, animationDelay: g.d }}
          viewBox="0 0 40 14"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        >
          <path d="M2 10 C7 2 11 2 16 9 C21 2 25 2 30 10" />
        </svg>
      ))}

      {/* twinkling stars */}
      {STARS.map((s, i) => (
        <div
          key={i}
          className="absolute rounded-full bg-sand-100 twinkle"
          style={{ left: s.left, top: s.top, width: s.s, height: s.s, animationDelay: s.d }}
        />
      ))}

      {/* moonlit pool of water — separates the ship from the dark sea */}
      <div
        className={`absolute ${shipPos} bottom-[60px] sm:bottom-[64px] z-[6] w-[420px] h-[150px]`}
        style={{ background: 'radial-gradient(ellipse 50% 55% at 50% 50%, rgba(95,189,208,.20), rgba(95,189,208,.06) 45%, transparent 70%)' }}
      />

      {/* moonlit galleon riding the swell */}
      <div className={`absolute ${shipPos} bottom-[120px] sm:bottom-[128px] z-[7] w-[210px] sm:w-[260px] ship`}>
        <svg viewBox="0 0 260 200" className="w-full h-full" fill="none">
          <g stroke="#06222b" strokeWidth="0.8" opacity="0.5">
            <path d="M128,26 L40,148 M128,26 L226,148 M196,54 L250,144 M68,66 L36,120" />
          </g>
          <g stroke="#06222b" strokeWidth="3" strokeLinecap="round">
            <line x1="68" y1="122" x2="68" y2="66" />
            <line x1="128" y1="150" x2="128" y2="26" />
            <line x1="196" y1="150" x2="196" y2="54" />
          </g>
          <g fill="#14586b">
            <path d="M68,72 L68,124 L40,116 L40,84 Z" />
            <path d="M98,82 L158,82 Q164,101 160,120 Q128,127 96,120 Q92,101 98,82 Z" />
            <path d="M108,48 L148,48 Q154,63 150,78 Q128,83 106,78 Q102,63 108,48 Z" />
            <path d="M176,92 L216,92 Q221,108 218,124 Q196,130 174,124 Q171,108 176,92 Z" />
            <path d="M183,64 L209,64 Q213,76 211,88 Q196,93 181,88 Q179,76 183,64 Z" />
            <path d="M250,142 L214,92 L224,148 Z" />
          </g>
          <g stroke="#06222b" strokeWidth="2" strokeLinecap="round">
            <line x1="94" y1="82" x2="162" y2="82" />
            <line x1="104" y1="48" x2="152" y2="48" />
            <line x1="172" y1="92" x2="220" y2="92" />
            <line x1="179" y1="64" x2="213" y2="64" />
          </g>
          <g fill="none" stroke="#E5CC8F" strokeWidth="1.3" opacity="0.55" strokeLinecap="round">
            <path d="M98,82 L158,82 M108,48 L148,48 M176,92 L216,92 M183,64 L209,64 M68,72 L68,124" />
          </g>
          <g fill="#C9A55C">
            <path d="M128,26 L149,31 L128,36 Z" />
            <path d="M196,54 L213,58 L196,62 Z" opacity="0.9" />
            <path d="M68,66 L84,70 L68,74 Z" opacity="0.9" />
          </g>
          <path d="M36,148 L228,148 L210,180 Q130,196 54,180 Z" fill="#06222b" />
          <path d="M30,121 Q30,119 33,119 L60,119 L60,150 L30,150 Z" fill="#0a313d" />
          <g fill="#E5CC8F" opacity="0.75">
            <rect x="37" y="128" width="5" height="6" rx="1" />
            <rect x="48" y="128" width="5" height="6" rx="1" />
          </g>
          <path d="M224,150 L252,139 L228,154 Z" fill="#06222b" />
          <path d="M36,148 L228,148 M30,121 L60,121" stroke="#E5CC8F" strokeWidth="1" opacity="0.45" />
          {/* broadside cannons on the flank */}
          <path d="M52,158 Q130,166 208,158" fill="none" stroke="#E5CC8F" strokeWidth="1" opacity="0.3" />
          <g fill="#02161c">
            <rect x="72" y="153" width="8" height="6" rx="1.2" /><rect x="100" y="154" width="8" height="6" rx="1.2" /><rect x="128" y="155" width="8" height="6" rx="1.2" /><rect x="156" y="154" width="8" height="6" rx="1.2" /><rect x="184" y="153" width="8" height="6" rx="1.2" />
          </g>
          <g stroke="#16627a" strokeWidth="3.4" strokeLinecap="round">
            <line x1="76" y1="159" x2="79" y2="167" /><line x1="104" y1="160" x2="107" y2="168" /><line x1="132" y1="161" x2="135" y2="169" /><line x1="160" y1="160" x2="163" y2="168" /><line x1="188" y1="159" x2="191" y2="167" />
          </g>
          <g fill="#E5CC8F" opacity="0.6">
            <circle cx="79" cy="167" r="1.4" /><circle cx="107" cy="168" r="1.4" /><circle cx="135" cy="169" r="1.4" /><circle cx="163" cy="168" r="1.4" /><circle cx="191" cy="167" r="1.4" />
          </g>
          {/* wake / waterline foam at the hull base */}
          <g stroke="#E5CC8F" strokeWidth="1.6" opacity="0.55" strokeLinecap="round" fill="none">
            <path d="M20,184 Q44,189 72,186" />
            <path d="M192,186 Q220,189 244,184" />
          </g>
        </svg>
      </div>

      {/* layered animated swell */}
      <div className="absolute bottom-0 left-0 right-0 z-[6]">
        <div className="wave-layer w-[200%]" style={{ animationDuration: '26s' }}>
          <WaveBack /><WaveBack />
        </div>
        <div className="wave-layer w-[200%] -mt-8" style={{ animationDuration: '18s', animationDirection: 'reverse' }}>
          <WaveMid /><WaveMid />
        </div>
        <div className="wave-layer w-[200%] -mt-10" style={{ animationDuration: '13s' }}>
          <WaveFront /><WaveFront />
        </div>
      </div>

      {/* vignette */}
      <div className="absolute inset-0 sea-vignette" />
    </div>
  )
}

const WaveBack = () => (
  <svg viewBox="0 0 1440 120" className="w-1/2 inline-block align-bottom" preserveAspectRatio="none">
    <path d="M0,60 C180,20 360,100 540,70 C720,40 900,110 1080,75 C1260,45 1380,85 1440,70 L1440,120 L0,120 Z" fill="#0c4253" opacity="0.75" />
  </svg>
)
const WaveMid = () => (
  <svg viewBox="0 0 1440 130" className="w-1/2 inline-block align-bottom" preserveAspectRatio="none">
    <path d="M0,70 C200,30 380,110 600,80 C820,50 980,120 1200,82 C1320,60 1400,90 1440,80 L1440,130 L0,130 Z" fill="#0a3743" opacity="0.9" />
  </svg>
)
const WaveFront = () => (
  <svg viewBox="0 0 1440 140" className="w-1/2 inline-block align-bottom" preserveAspectRatio="none">
    <path d="M0,84 C220,44 420,120 660,90 C900,60 1040,128 1260,92 C1360,74 1410,96 1440,90 L1440,140 L0,140 Z" fill="#062029" />
    <path d="M0,84 C220,44 420,120 660,90 C900,60 1040,128 1260,92 C1360,74 1410,96 1440,90" fill="none" stroke="#E5CC8F" strokeWidth="1.4" opacity="0.18" />
  </svg>
)

const GULLS = [
  { left: '18%', top: '20%', w: '40px', o: 0.4, d: '0.4s' },
  { left: '26%', top: '15%', w: '28px', o: 0.3, d: '1.1s' },
  { left: '80%', top: '24%', w: '32px', o: 0.3, d: '0.8s' },
]
const STARS = [
  { left: '72%', top: '12%', s: '4px', d: '0.2s' },
  { left: '40%', top: '9%', s: '3px', d: '1.4s' },
  { left: '88%', top: '18%', s: '2px', d: '2.1s' },
]

const BACKDROP_CSS = `
  :root { --abyss:#04161c; }
  .sea-bg{ background:radial-gradient(130% 95% at 50% -12%, #11697f 0%, #0c5267 26%, #0A3E4F 52%, #072a35 78%, var(--abyss) 100%); }
  .sea-vignette{ background:radial-gradient(120% 80% at 50% 22%, transparent 42%, rgba(4,18,24,.55) 100%); }
  .chart-grid{ background-image:linear-gradient(to right,#5FBDD0 1px,transparent 1px),linear-gradient(to bottom,#5FBDD0 1px,transparent 1px); background-size:7.5% 7.5%; }
  .grain{ background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E"); }
  @keyframes waveDrift { from{transform:translateX(0)} to{transform:translateX(-50%)} }
  @keyframes slowSpin  { from{transform:rotate(0)} to{transform:rotate(360deg)} }
  @keyframes bob       { 0%,100%{transform:translateY(0) rotate(-1.2deg)} 50%{transform:translateY(-10px) rotate(1.2deg)} }
  @keyframes gullbob   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
  @keyframes twinkle   { 0%,100%{opacity:.25} 50%{opacity:.9} }
  .wave-layer{ animation:waveDrift linear infinite; will-change:transform; }
  .compass-rose{ animation:slowSpin 160s linear infinite; transform-origin:center; }
  .ship{ animation:bob 7s ease-in-out infinite; transform-origin:center bottom; }
  .gull{ animation:gullbob 5s ease-in-out infinite; }
  .twinkle{ animation:twinkle 4s ease-in-out infinite; }
  @media (prefers-reduced-motion: reduce){
    .wave-layer,.compass-rose,.ship,.gull,.twinkle{ animation:none !important; }
  }
`
